import dayjs from "dayjs";
import type { PitchingEligibility, FilterRange, Platform, Talent } from "../../shared/types";
import { getCountryFromLabelOrString } from "../countries";
import { isNumberInRange } from "../core";
import { timestampOrDateToDate } from "../dates";
import { abbreviateNumber } from "../formatNumbers";

type FollowerRange = { lower: number | null; upper: number | null };

const DEFAULT_PCT_RANGE = { lower: 0, upper: 100 };
const EMPTY_FOLLOWER_RANGE: FilterRange = { lower: null, upper: null };
const ALL_PLATFORMS: Platform[] = ["TikTok", "Instagram", "YouTube"];

/**
 * Full eligibility snapshot for Firestore merge. Every key is written so nested-map
 * merge cannot leave a stale chip or platform range behind.
 * Defaults here are unconstrained ({@link eligibilityHasConstraints} is false).
 */
export function normalizeEligibility(
    eligibility?: PitchingEligibility | null,
): PitchingEligibility {
    const source = eligibility ?? {};
    const followersByPlatform = Object.fromEntries(
        ALL_PLATFORMS.map((platform) => [
            platform,
            source.followersByPlatform?.[platform] ?? { ...EMPTY_FOLLOWER_RANGE },
        ]),
    ) as Record<Platform, FilterRange>;

    return {
        contentLanguage: source.contentLanguage ?? [],
        gender: source.gender ?? [],
        favoriteGenre: source.favoriteGenre ?? [],
        contentTags: source.contentTags ?? [],
        tagFilterLogic: source.tagFilterLogic ?? "OR",
        cities: source.cities ?? [],
        countries: source.countries ?? [],
        maleFollowerPctRange: source.maleFollowerPctRange ?? { ...DEFAULT_PCT_RANGE },
        femaleFollowerPctRange: source.femaleFollowerPctRange ?? { ...DEFAULT_PCT_RANGE },
        topFollowerCountries: source.topFollowerCountries ?? [],
        topFollowerAgeBrackets: source.topFollowerAgeBrackets ?? [],
        emailInBio: source.emailInBio ?? "Any",
        followersByPlatform,
    };
}

function talentIsOnPlatform(talent: Talent, platform: Platform): boolean {
    return Boolean(talent.platformData?.[platform]?.username);
}

function talentIsOnAnyCampaignPlatform(
    talent: Talent,
    campaignPlatforms: Platform[] | undefined,
): boolean {
    if (!campaignPlatforms?.length) {
        return true;
    }
    return campaignPlatforms.some((platform) => talentIsOnPlatform(talent, platform));
}

function rangeIsActive(range: FollowerRange | undefined | null): boolean {
    return range != null && (range.lower != null || range.upper != null);
}

function numberMatchesRange(value: number, range: FollowerRange): boolean {
    const minOk = range.lower == null || range.lower <= value;
    const maxOk = range.upper == null || value <= range.upper;
    return minOk && maxOk;
}

function pctRangeIsActive(
    range: { lower: number; upper: number } | undefined,
): boolean {
    if (!range) {
        return false;
    }
    return range.lower !== DEFAULT_PCT_RANGE.lower || range.upper !== DEFAULT_PCT_RANGE.upper;
}

function grailEmailRecentlyInBio(talent: Talent): boolean {
    const tiktok = talent.platformData?.TikTok;
    if (!tiktok?.grailEmailInBio || !tiktok.lastSuccessfulCrawl) {
        return false;
    }
    return dayjs(timestampOrDateToDate(tiktok.lastSuccessfulCrawl)).isAfter(
        dayjs().subtract(14, "day"),
    );
}

function matchesFollowers(
    talent: Talent,
    eligibility: PitchingEligibility,
): boolean {
    const byPlatform = eligibility.followersByPlatform;
    const activePlatforms = (Object.keys(byPlatform || {}) as Platform[]).filter(
        (platform) => rangeIsActive(byPlatform?.[platform]),
    );
    if (!activePlatforms.length) {
        return true;
    }
    return activePlatforms.every((platform) => {
        const followers = talent.platformData?.[platform]?.followers || 0;
        return numberMatchesRange(followers, byPlatform![platform]!);
    });
}

function matchesAudience(
    talent: Talent,
    eligibility: PitchingEligibility,
    campaignPlatforms: Platform[] | undefined,
): boolean {
    const audiencePlatform = campaignPlatforms?.[0] || "TikTok";
    const audienceData = talent.platformData?.[audiencePlatform];

    if (pctRangeIsActive(eligibility.maleFollowerPctRange)) {
        if (
            typeof audienceData?.maleFollowerPct !== "number" ||
            !isNumberInRange(
                audienceData.maleFollowerPct,
                eligibility.maleFollowerPctRange!.lower,
                eligibility.maleFollowerPctRange!.upper,
            )
        ) {
            return false;
        }
    }

    if (pctRangeIsActive(eligibility.femaleFollowerPctRange)) {
        if (
            typeof audienceData?.femaleFollowerPct !== "number" ||
            !isNumberInRange(
                audienceData.femaleFollowerPct,
                eligibility.femaleFollowerPctRange!.lower,
                eligibility.femaleFollowerPctRange!.upper,
            )
        ) {
            return false;
        }
    }

    if (eligibility.topFollowerCountries?.length) {
        const topFollowerLocation = getCountryFromLabelOrString(
            audienceData?.topFollowerLocation,
        );
        if (!eligibility.topFollowerCountries.includes(topFollowerLocation || "")) {
            return false;
        }
    }

    if (eligibility.topFollowerAgeBrackets?.length) {
        if (
            !eligibility.topFollowerAgeBrackets.includes(
                audienceData?.topFollowerAgeBracket || "",
            )
        ) {
            return false;
        }
    }

    return true;
}

/**
 * Hard-block matcher for opportunity pitching. A creator must be on any selected
 * campaign platform, then match every set eligibility chip (AND). Price is not
 * part of eligibility; validate with {@link pitchPriceWithinCampaignBounds}.
 */
export function talentMatchesPitchingEligibility(
    talent: Talent,
    eligibility: PitchingEligibility | undefined | null,
    campaignPlatforms?: Platform[],
): boolean {
    if (!talentIsOnAnyCampaignPlatform(talent, campaignPlatforms)) {
        return false;
    }
    if (!eligibility) {
        return true;
    }

    if (eligibility.contentTags?.length) {
        const talContentTagsLower = (talent.contentTags || []).map((tag) =>
            tag.toLowerCase(),
        );
        const matchesTag = (tag: string) =>
            (talent.contentTags || []).includes(tag) ||
            talContentTagsLower.includes(tag.toLowerCase());
        const tagsOk =
            eligibility.tagFilterLogic === "AND"
                ? eligibility.contentTags.every(matchesTag)
                : eligibility.contentTags.some(matchesTag);
        if (!tagsOk) {
            return false;
        }
    }

    if (eligibility.contentLanguage?.length) {
        const languageOk =
            eligibility.contentLanguage.some(
                (language) => talent.contentLanguage === language,
            ) ||
            Boolean(
                eligibility.contentLanguage.includes("Other") &&
                    (talent.contentLanguage?.includes("Other") || !talent.contentLanguage),
            );
        if (!languageOk) {
            return false;
        }
    }

    if (eligibility.favoriteGenre?.length) {
        const genreOk =
            eligibility.favoriteGenre.some((genre) => talent.favoriteGenre === genre) ||
            Boolean(
                eligibility.favoriteGenre.includes("Other") &&
                    (talent.favoriteGenre?.includes("Other") || !talent.favoriteGenre),
            );
        if (!genreOk) {
            return false;
        }
    }

    if (eligibility.cities?.length) {
        if (!eligibility.cities.some((city) => (talent.cities || []).includes(city))) {
            return false;
        }
    }

    if (eligibility.countries?.length) {
        if (!talent.country || !eligibility.countries.includes(talent.country)) {
            return false;
        }
    }

    if (eligibility.gender?.length) {
        const genderOk =
            eligibility.gender.some((gender) => talent.gender === gender) ||
            Boolean(
                eligibility.gender.includes("Other") && talent.gender?.includes("Other"),
            );
        if (!genderOk) {
            return false;
        }
    }

    if (!matchesFollowers(talent, eligibility)) {
        return false;
    }

    if (!matchesAudience(talent, eligibility, campaignPlatforms)) {
        return false;
    }

    if (eligibility.emailInBio && eligibility.emailInBio !== "Any") {
        const emailRecentlyInBio = grailEmailRecentlyInBio(talent);
        if (eligibility.emailInBio === "Yes" && !emailRecentlyInBio) {
            return false;
        }
        if (eligibility.emailInBio === "No" && emailRecentlyInBio) {
            return false;
        }
    }

    return true;
}

export function pitchPriceWithinCampaignBounds(
    price: number,
    minPricePerCreator?: number | null,
    maxPricePerCreator?: number | null,
): boolean {
    if (typeof price !== "number" || Number.isNaN(price)) {
        return false;
    }
    if (minPricePerCreator != null && price < minPricePerCreator) {
        return false;
    }
    if (maxPricePerCreator != null && price > maxPricePerCreator) {
        return false;
    }
    return true;
}

/** Pre-submit helper: eligibility snapshot plus campaign min/max price. */
export function isPitchSubmittable(args: {
    talent: Talent;
    eligibility?: PitchingEligibility | null;
    campaignPlatforms?: Platform[];
    price: number;
    minPricePerCreator?: number | null;
    maxPricePerCreator?: number | null;
}): boolean {
    return (
        talentMatchesPitchingEligibility(
            args.talent,
            args.eligibility,
            args.campaignPlatforms,
        ) &&
        pitchPriceWithinCampaignBounds(
            args.price,
            args.minPricePerCreator,
            args.maxPricePerCreator,
        )
    );
}

const PLATFORM_PILL_PREFIX: Record<Platform, string> = {
    TikTok: "TT",
    Instagram: "IG",
    YouTube: "YT",
};

function listIsSet(values?: string[] | null): values is string[] {
    return Boolean(values?.length);
}

function followerNoun(platform: Platform) {
    return platform === "YouTube" ? "Subscribers" : "Followers";
}

function formatBound(n: number) {
    return String(abbreviateNumber(n));
}

function rangePillLabel(noun: string, range: FilterRange | undefined) {
    const lower = range?.lower ?? null;
    const upper = range?.upper ?? null;
    if (lower == null && upper == null) {
        return null;
    }
    if (lower != null && upper != null) {
        return `${noun} ${formatBound(lower)}–${formatBound(upper)}`;
    }
    if (lower != null) {
        return `${noun} ${formatBound(lower)}+`;
    }
    return `${noun} up to ${formatBound(upper!)}`;
}

/** True when any hard-block chip is set (not the empty / default snapshot). */
export function eligibilityHasConstraints(
    eligibility?: PitchingEligibility | null,
): boolean {
    if (!eligibility) {
        return false;
    }
    if (
        listIsSet(eligibility.contentLanguage) ||
        listIsSet(eligibility.gender) ||
        listIsSet(eligibility.favoriteGenre) ||
        listIsSet(eligibility.contentTags) ||
        listIsSet(eligibility.cities) ||
        listIsSet(eligibility.countries) ||
        listIsSet(eligibility.topFollowerCountries) ||
        listIsSet(eligibility.topFollowerAgeBrackets)
    ) {
        return true;
    }
    if (eligibility.emailInBio && eligibility.emailInBio !== "Any") {
        return true;
    }
    if (eligibility.tagFilterLogic === "AND" && listIsSet(eligibility.contentTags)) {
        return true;
    }
    if (pctRangeIsActive(eligibility.maleFollowerPctRange)) {
        return true;
    }
    if (pctRangeIsActive(eligibility.femaleFollowerPctRange)) {
        return true;
    }
    const byPlatform = eligibility.followersByPlatform;
    if (!byPlatform) {
        return false;
    }
    return (Object.keys(byPlatform) as Platform[]).some((platform) =>
        rangeIsActive(byPlatform[platform]),
    );
}

/** Summary pills for create/settings and the pitching eligibility strip. */
export function eligibilityConstraintLabels(
    eligibility?: PitchingEligibility | null,
): string[] {
    if (!eligibilityHasConstraints(eligibility) || !eligibility) {
        return [];
    }

    const labels: string[] = [];

    if (listIsSet(eligibility.countries)) {
        labels.push(
            eligibility.countries.length === 1
                ? eligibility.countries[0]
                : `Countries: ${eligibility.countries.length}`,
        );
    }
    if (listIsSet(eligibility.cities)) {
        labels.push(
            eligibility.cities.length === 1
                ? eligibility.cities[0]
                : `Cities: ${eligibility.cities.length}`,
        );
    }
    if (listIsSet(eligibility.gender)) {
        labels.push(
            eligibility.gender.length === 1
                ? eligibility.gender[0]
                : `Genders: ${eligibility.gender.length}`,
        );
    }
    if (listIsSet(eligibility.contentLanguage)) {
        labels.push(
            eligibility.contentLanguage.length === 1
                ? eligibility.contentLanguage[0]
                : `Languages: ${eligibility.contentLanguage.length}`,
        );
    }
    if (listIsSet(eligibility.favoriteGenre)) {
        labels.push(
            eligibility.favoriteGenre.length === 1
                ? eligibility.favoriteGenre[0]
                : `Genres: ${eligibility.favoriteGenre.length}`,
        );
    }
    if (listIsSet(eligibility.contentTags)) {
        const tagLogic =
            eligibility.tagFilterLogic === "AND" ? "All tags" : "Tags";
        labels.push(
            eligibility.contentTags.length === 1
                ? eligibility.contentTags[0]
                : `${tagLogic}: ${eligibility.contentTags.length}`,
        );
    }
    if (pctRangeIsActive(eligibility.femaleFollowerPctRange)) {
        labels.push(
            `Female follower % ${eligibility.femaleFollowerPctRange!.lower}–${eligibility.femaleFollowerPctRange!.upper}`,
        );
    }
    if (pctRangeIsActive(eligibility.maleFollowerPctRange)) {
        labels.push(
            `Male follower % ${eligibility.maleFollowerPctRange!.lower}–${eligibility.maleFollowerPctRange!.upper}`,
        );
    }
    if (listIsSet(eligibility.topFollowerCountries)) {
        labels.push(
            eligibility.topFollowerCountries.length === 1
                ? `Top country ${eligibility.topFollowerCountries[0]}`
                : `Top countries: ${eligibility.topFollowerCountries.length}`,
        );
    }
    if (listIsSet(eligibility.topFollowerAgeBrackets)) {
        labels.push(
            eligibility.topFollowerAgeBrackets.length === 1
                ? `Top age ${eligibility.topFollowerAgeBrackets[0]}`
                : `Top ages: ${eligibility.topFollowerAgeBrackets.length}`,
        );
    }
    if (eligibility.emailInBio && eligibility.emailInBio !== "Any") {
        labels.push(`Email in bio: ${eligibility.emailInBio}`);
    }

    const byPlatform = eligibility.followersByPlatform;
    if (byPlatform) {
        (Object.keys(byPlatform) as Platform[]).forEach((platform) => {
            const label = rangePillLabel(
                `${PLATFORM_PILL_PREFIX[platform]} ${followerNoun(platform)}`,
                byPlatform[platform],
            );
            if (label) {
                labels.push(label);
            }
        });
    }

    return labels;
}
